'use client';

import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC } from 'constants/layout';
import { ModalParams, ModalType } from 'constants/modal';
import { ZIndex } from 'constants/zIndex';
import { CloseIcon } from 'icons/CloseIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { IconButton } from 'ui-components/common/IconButton';
import { Link } from 'ui-components/common/Link';
import { ModalHandler } from 'utils/ModalHandler';

const TOAST_ANIMATED_TIME = 0.4; // 0.4 sec
const TOAST_STALE_TIME = 1; // 1 sec

export const Toast = () => {
  const timerRef = useRef<NodeJS.Timeout>(undefined);
  // const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [isShow, setIsShow] = useState(false);
  const [state, setState] = useState<ModalParams[ModalType.Toast]>();

  const handleHide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = undefined;
    state?.hideCallback?.();
    setIsShow(false);
  }, [state, timerRef]);

  useEffect(() => {
    // Link 가 있다면 클릭할 시간을 줘야 하기 때문에 TOAST_STALE_TIME 에 5배 한다.
    const extraTime = state?.link || state?.externalLink ? 5 : 1;
    if (isShow) {
      if (state?.isManualHide) {
        return;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      const timer = setTimeout(
        () => {
          handleHide();
        },
        (TOAST_ANIMATED_TIME * 2 + TOAST_STALE_TIME * extraTime + (state?.extraShowTimeSec ?? 0)) * 1000,
      );
      timerRef.current = timer;
    }
  }, [handleHide, isShow, state]);

  useEffect(() => {
    const listener = (showModal: boolean, params?: ModalParams[ModalType.Toast]) => {
      setIsShow(showModal);
      setState(params);
    };
    ModalHandler.addEventListener(ModalType.Toast, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.Toast);
    };
  }, []);

  return (
    <StyledToast isShow={isShow}>
      <StyledWrapper>
        <div>
          <StyledDescription>{state?.description}</StyledDescription>
          {state?.externalLink && (
            <StyledLink>
              <ExternalLink href={state?.externalLink?.url}>{state?.externalLink?.text}</ExternalLink>
            </StyledLink>
          )}
          {state?.link && (
            <StyledLink>
              {state?.link?.url && <Link href={state.link.url}>{state?.link?.text}</Link>}
              {state?.link?.onClick && (
                <button type="button" onClick={state.link.onClick}>
                  {state?.link?.text}
                </button>
              )}
            </StyledLink>
          )}
        </div>
        <StyledIconButton onClick={handleHide}>
          <CloseIcon color={Color.White} />
        </StyledIconButton>
      </StyledWrapper>
    </StyledToast>
  );
};

const StyledToast = styled.div(
  ({ isShow }: { isShow: boolean }) => css`
    z-index: ${ZIndex.Modal};
    width: calc(100% - 40px);
    max-width: 400px;
    position: fixed;
    bottom: var(--safe-area-bottom-old);
    bottom: var(--safe-area-bottom);
    left: 50%;
    opacity: ${isShow ? 1 : 0};
    pointer-events: ${isShow ? 'initial' : 'none'};

    transform: translate(-50%, ${isShow ? '-50%' : '100%'});
    transition:
      opacity ${TOAST_ANIMATED_TIME}s ease-in-out,
      transform ${TOAST_ANIMATED_TIME}s ease-in-out;

    @media (${isPC}) {
      transform: translate(-50%, ${isShow ? '50%' : '-100%'});
      bottom: initial;
      top: var(--safe-area-top-old);
      top: var(--safe-area-top);
    }
  `,
);

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${Color.Gray800};
  color: ${Color.White};
  padding: 13px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StyledDescription = styled.p`
  text-align: left;
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Regular};
  word-break: break-all;
  white-space: break-spaces;
  word-wrap: anywhere;
`;

const StyledLink = styled.div`
  color: ${Color.Primary300};
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Regular};
`;

const StyledIconButton = styled(IconButton)`
  width: 16px;
  height: 16px;
`;
